import { useCallback, useMemo, useState } from 'react'
import { useAppSelector } from '@/hooks/redux'
import type { AuthIntent } from '@/lib/postLoginRedirect'

/**
 * Reading is public across the app. Anything that leaves a footprint (a vote,
 * a validation, a comment, a like, a report) needs a session, and this hook is
 * the single place that enforces it in the UI.
 *
 * Wrap a handler in `guard` and it runs untouched for a signed-in user. For a
 * guest it opens the sign-in prompt instead, carrying the current page and the
 * intent so they land back here with the right thing already open.
 *
 *   const { guard, promptProps } = useAuthGate()
 *   <IdeaCard onToggleUpvote={guard('vote-up', handleUpvote)} />
 *   <AuthPromptModal {...promptProps} />
 *
 * The server is still the real gate: every one of these endpoints rejects an
 * unauthenticated write. The prompt just means nobody fills in a form only to
 * be handed a 401.
 */

type PromptCopy = { title: string; description: string }

const COPY: Record<AuthIntent, PromptCopy> = {
  validate: {
    title: 'Sign in to validate',
    description:
      'Your name goes on every rating, so we need to know who you are. Log in or create an account to start validating.',
  },
  comment: {
    title: 'Sign in to comment',
    description:
      'Founders want to know who the feedback is from. Log in or create an account to join the thread.',
  },
  'vote-up': {
    title: 'Sign in to vote',
    description:
      'Votes shape which ideas rise, so each one has to belong to someone. Log in or create an account to cast yours.',
  },
  'vote-down': {
    title: 'Sign in to vote',
    description:
      'Votes shape which ideas rise, so each one has to belong to someone. Log in or create an account to cast yours.',
  },
  'like-comment': {
    title: 'Sign in to react',
    description:
      'Log in or create an account to like comments and join the conversation.',
  },
  'submit-idea': {
    title: 'Sign in to submit your idea',
    description:
      'Your idea needs an owner so we can send you the validations and the report. Create an account and start writing.',
  },
}

type GateState = {
  intent: AuthIntent
  returnTo?: string
} | null

export function useAuthGate() {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated)
  const [gate, setGate] = useState<GateState>(null)

  const close = useCallback(() => setGate(null), [])

  /** Opens the prompt directly, for places without a handler to wrap. */
  const prompt = useCallback(
    (intent: AuthIntent, returnTo?: string) => setGate({ intent, returnTo }),
    [],
  )

  /**
   * Returns `handler` when there is a session, and a prompt-opener when there
   * is not. The guest branch never calls `handler`, so no optimistic update
   * fires and no request goes out.
   */
  const guard = useCallback(
    <A extends unknown[]>(
      intent: AuthIntent,
      handler: (...args: A) => void,
      returnTo?: string,
    ) => {
      if (isAuthenticated) return handler
      return (..._args: A) => {
        void _args
        setGate({ intent, returnTo })
      }
    },
    [isAuthenticated],
  )

  const promptProps = useMemo(
    () => ({
      open: gate != null,
      onClose: close,
      title: gate ? COPY[gate.intent].title : undefined,
      description: gate ? COPY[gate.intent].description : undefined,
      returnTo: gate?.returnTo,
      intent: gate?.intent ?? null,
    }),
    [gate, close],
  )

  return { isAuthenticated, guard, prompt, promptProps }
}
